import { BidWorkflow } from "lib/hooks/api/bid/bids/useBidWorkflow";
import { LeadWorkflow } from "lib/hooks/api/leads/useLeadWorkflow";
import { CreateWorkflow_Transitions } from "../../../lib/generated/app-service-gql/graphql";
import { IconProps } from "../../../lib/icons/untitled_ui/UIcon";

type Stage = {
  id: string;
  name: string;
  color: string;
  iconColor: string;
  iconName: string;
  count: number;
};

export type WorkflowTransition = {
  id: string;
  group: string;
  order: number;
  stage: Stage;
};

export type Workflow = {
  id: string;
  isDefault: boolean;
  transitions: WorkflowTransition[];
};

export type StageGroup = {
  name: string;
  transitions: WorkflowTransition[];
};

export type GroupedWorkflow = {
  workflowId: string;
  isDefault: boolean;
  groups: StageGroup[];
};

export function mapBidWorkflowToWorkflow(bidWorkflow: BidWorkflow): Workflow {
  return {
    ...bidWorkflow,
    transitions: bidWorkflow.transitions.map((transition) => ({
      ...transition,
      stage: {
        ...transition.stage,
        count: transition.stage.bidCount ?? 0,
      },
    })),
  };
}

export function mapLeadWorkflowToWorkflow(leadWorkflow: LeadWorkflow): Workflow {
  return {
    ...leadWorkflow,
    transitions: leadWorkflow.transitions.map((transition) => ({
      ...transition,
      stage: {
        ...transition.stage,
        count: transition.stage.leadCount ?? 0,
      },
    })),
  };
}

export function groupWorkflowTransitions(workflow?: Workflow): GroupedWorkflow | undefined {
  if (!workflow) return;
  const groupsByName = [...workflow.transitions]
    .sort((a, b) => a.order - b.order)
    .reduce<Record<string, StageGroup>>((acc, transition) => {
      const group = transition.group;

      if (!acc[group]) {
        acc[group] = {
          name: group,
          transitions: [],
        };
      }

      acc[group].transitions.push(transition);

      return acc;
    }, {});

  return {
    workflowId: workflow.id,
    isDefault: workflow.isDefault,
    groups: Object.values(groupsByName),
  };
}

export type NewTransition = {
  id?: string;
  stageId?: string;
  stageName: string;
  stageColor: string;
  stageIconColor: string;
  stageIconName: string;
  order: number;
  group: string;
};

export type CustomiseWorkflowInput = {
  workflowId: string;
  isDefault: boolean;
  transitions: NewTransition[];
  stageReplacements: Array<{
    oldStageId: string;
    newStageName: string;
  }>;
};

export type WorkflowFormValues = {
  transitions: NewTransition[];
};

// Values for a new stage which are being chosen in the form
export type NewStage = {
  id?: string;
  name: string;
  iconColor: string;
  backgroundColor: string;
};

export function transitionToInput(t: WorkflowTransition): NewTransition {
  return {
    id: t.id,
    stageId: t.stage.id,
    stageName: t.stage.name,
    stageColor: t.stage.color,
    stageIconColor: t.stage.iconColor,
    stageIconName: t.stage.iconName,
    order: t.order,
    group: t.group,
  };
}

/**
 * Strips the internal `id` and `stageId` fields from a NewTransition so it
 * satisfies the CreateWorkflow_Transitions schema expected by the backend.
 * Those fields must be omitted when creating a workflow — passing them causes
 * Zod validation to fail because the backend generates new IDs itself.
 */
export function toCreateTransition(t: NewTransition): CreateWorkflow_Transitions {
  return {
    group: t.group,
    order: t.order,
    stageName: t.stageName,
    stageColor: t.stageColor,
    stageIconColor: t.stageIconColor,
    stageIconName: t.stageIconName,
  };
}

/**
 * Will be called on the edit transitions, just to enforce them as proper WorkflowTransition objects with
 * all necessary fields, as the form values (NewTransition) are more flexible and may not have all fields filled
 *  in (e.g. stageId is only present on existing stages, not new ones)
 * @param t
 * @returns
 */
export function newTransitionToWorkflowTransition(t: NewTransition): WorkflowTransition {
  return {
    id: t.id ?? "",
    stage: {
      id: t.stageId ?? "",
      name: t.stageName,
      color: t.stageColor,
      iconColor: t.stageIconColor,
      iconName: t.stageIconName,
      count: 0, // Default count, can be updated later
    },
    order: t.order,
    group: t.group,
  };
}

export function createCustomiseWorkflowInput(
  workflow: GroupedWorkflow,
  transitions: NewTransition[],
): CustomiseWorkflowInput {
  return {
    workflowId: workflow.workflowId,
    isDefault: workflow.isDefault,
    transitions,
    stageReplacements: transitions.flatMap((t) =>
      t.stageId !== undefined ? [{ oldStageId: t.stageId, newStageName: t.stageName }] : [],
    ),
  };
}

/**
 * Takes an icon color & applies 15% opacity for the background color, which is used in the stage pill in the UI.
 * @param iconColor
 * @returns
 */
const makeColor = (iconColor: string): StageColor => ({
  stageIconColor: iconColor,
  stageBackgroundColor: `${iconColor}26`,
});

export const STAGE_COLORS: StageColor[] = [
  "#00DFDF",
  "#0070E0",
  "#9E01FF",
  "#FF0059",
  "#FF8000",
  "#FFCA09",
  "#2BAB55",
  "#67798B",
].map((c) => makeColor(c));

export type StageColor = {
  stageIconColor: string;
  stageBackgroundColor: string;
};

export function getGroupIcon(group: StageGroup): IconProps["icon"] {
  return (
    (group.transitions.find((t) => t.stage.iconName)?.stage.iconName as IconProps["icon"]) ||
    "circleProgress50"
  );
}
